import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { call, classes } from 'utils';
import { useDelay, useFrameSize } from 'hooks';

import styles from './Overlay.module.scss';

const ANIMATION_DURATION = 310;

type Props = {
  visible: boolean;
  children?: ReactNode | ((size?: Size) => ReactNode);
  onShow?: () => void;
  onClose?: () => void;
  onBlur?: () => void;
  delay?: number;
  ratio?: number;
  style?: CSSProperties;
};

type State = 'visible' | 'leaving' | 'hidden' | 'closed';

type Size = {
  width: number;
  height: number;
  full?: boolean | undefined;
};

const Overlay = (props: Props) => {
  const { visible, onShow, onClose } = props;
  const [state, setState] = useState<State>(visible ? 'visible' : 'hidden');

  const timer = useRef<NodeJS.Timeout>();
  const mounted = useRef(false);
  const delay = useDelay(visible ? props.delay || 0 : 0);

  useEffect(() => {
    if (visible) {
      if (state !== 'visible') {
        setState('visible');
      }
    } else if (state === 'visible') {
      setState('leaving');
      timer.current = setTimeout(() => setState('hidden'), ANIMATION_DURATION);
    } else if (state === 'hidden' && mounted.current) {
      setState('closed');
    }
  }, [visible, state]);

  useEffect(() => {
    if (state === 'closed' && mounted.current) {
      mounted.current = false;
      call(onClose);
    } else if (state === 'visible' && !mounted.current) {
      mounted.current = true;
      call(onShow);
    }
  }, [state, onClose, onShow]);

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  if (state === 'hidden' || state === 'closed' || delay) {
    return null;
  }

  const container = document.querySelector('body > #overlay');

  if (!container) {
    throw new Error('overlay container not defined');
  }

  return createPortal(<Frame {...props} state={state} />, container);
};

export default Overlay;

const Frame = (props: Props & { state: State }) => {
  const { state, ratio, onBlur, children } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const down = useRef<EventTarget>();

  const size = useFrameSize(containerRef, ratio);

  return (
    <div
      className={classes(styles.overlay, styles[state])}
      role="dialog"
      aria-modal={true}
      tabIndex={-1}
      ref={containerRef}
      onClick={e => {
        e.stopPropagation();

        if (e.target === down.current) {
          call(onBlur);
        }
      }}
      onMouseDown={e => (down.current = e.target)}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.preventDefault();
          call(onBlur);
        }
      }}
    >
      <div
        className={styles.content}
        onClick={e => e.stopPropagation()}
        style={{ ...size, ...props.style }}
      >
        {typeof children === 'function' ? children(size) : children}
      </div>
    </div>
  );
};
